import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../environments/environment';
import { ADMIN_PATH } from '../admin/admin-path';
import { LeadAttribution } from '../models/solar-lead.model';

export type TrackingConsent = 'granted' | 'denied';

const CONSENT_KEY = 'noya-tracking-consent';
const ATTRIBUTION_KEY = 'noya-attribution';
const VISIT_KEY = 'noya-visit-id';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

interface StoredAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  /** When the ad click was seen, needed to rebuild Meta's click id. */
  capturedAt: number;
}

type Fbq = (...args: unknown[]) => void;

/**
 * Ad attribution, the anonymous visit funnel and the Meta Pixel.
 * The campaign tags of the landing URL (utm_*) are kept for the session and sent with the
 * quote request, so each lead shows which ad produced it. Everything that identifies the
 * visitor to Meta (the pixel, its cookies, the click id) only exists after they accept.
 * The visit funnel is our own and anonymous: a random id kept for the tab's session, the
 * campaign tags and the furthest step reached. It feeds the admin dashboard.
 */
@Injectable({ providedIn: 'root' })
export class TrackingService {
  private router = inject(Router);
  private http = inject(HttpClient);

  /** null until the visitor answers the banner. */
  readonly consent = signal<TrackingConsent | null>(this.readConsent());

  private readonly attribution = this.captureAttribution();
  private readonly trackedLeads = new Set<string>();
  private readonly visitId = this.readVisitId();
  private readonly landingPath = location.pathname;
  private furthestStep = -1;
  private pixelStarted = false;
  private lastPath = location.pathname;

  constructor() {
    if (this.consent() === 'granted') {
      this.startPixel();
    }
    this.trackStep(0);
    // The wizard changes ?etape= on every step; only a real page change is a page view.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (location.pathname !== this.lastPath) {
        this.lastPath = location.pathname;
        this.fbq('track', 'PageView');
      }
    });
  }

  /** The admin pages show customer data and are never tracked. */
  static isPrivatePage(): boolean {
    return location.pathname.startsWith(`/${ADMIN_PATH}`);
  }

  setConsent(consent: TrackingConsent): void {
    this.consent.set(consent);
    try {
      localStorage.setItem(CONSENT_KEY, consent);
    } catch {
      // Storage disabled: the choice holds for this visit only.
    }
    if (consent === 'granted') {
      this.startPixel();
    }
  }

  /**
   * Records how far this visit went: 0 opened the site, 1 building chosen, 2 consumption
   * filled, 3 contact sent, 4 request confirmed. Fire-and-forget: it must never get in the
   * visitor's way, so failures are ignored.
   */
  trackStep(step: number): void {
    if (step <= this.furthestStep || TrackingService.isPrivatePage()) {
      return;
    }
    this.furthestStep = step;
    const a = this.attribution;
    this.http.post(`${environment.apiUrl}/api/v1/visits`, {
      visitId: this.visitId,
      step,
      landingPath: this.landingPath.slice(0, 100),
      referrerHost: this.referrerHost(),
      utmSource: a.utm_source,
      utmMedium: a.utm_medium,
      utmCampaign: a.utm_campaign,
      utmContent: a.utm_content,
      utmTerm: a.utm_term,
      // Only the fact that an ad was clicked; the click id itself needs consent.
      fromMetaAd: !!a.fbclid,
    }).subscribe({ error: () => undefined });
  }

  /** Attribution to send with a quote request. */
  leadFields(eventId: string): LeadAttribution {
    const a = this.attribution;
    const fields: LeadAttribution = {
      utmSource: a.utm_source,
      utmMedium: a.utm_medium,
      utmCampaign: a.utm_campaign,
      utmContent: a.utm_content,
      utmTerm: a.utm_term,
      visitId: this.visitId,
    };
    if (this.consent() !== 'granted') {
      return fields;
    }
    return {
      ...fields,
      fbclid: a.fbclid,
      fbc: this.cookie('_fbc') ?? (a.fbclid ? `fb.1.${a.capturedAt}.${a.fbclid}` : undefined),
      fbp: this.cookie('_fbp'),
      eventId,
      // Without the query string: it carries the wizard step and would differ between sends.
      pageUrl: location.origin + location.pathname,
      trackingConsent: true,
    };
  }

  /** Reports a lead to Meta once; the API sends the same event id from the server. */
  trackLead(eventId: string): void {
    if (this.trackedLeads.has(eventId) || !this.pixelStarted) {
      return;
    }
    this.trackedLeads.add(eventId);
    this.fbq('track', 'Lead', {}, { eventID: eventId });
  }

  private startPixel(): void {
    if (this.pixelStarted || !environment.metaPixelId || TrackingService.isPrivatePage()) {
      return;
    }
    this.pixelStarted = true;
    const w = window as unknown as { fbq?: any; _fbq?: any };
    if (!w.fbq) {
      // Meta's standard loader: calls are queued until fbevents.js arrives.
      const fbq: any = function (...args: unknown[]) {
        fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      w.fbq = fbq;
      w._fbq ??= fbq;
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    this.fbq('init', environment.metaPixelId);
    this.fbq('track', 'PageView');
  }

  private fbq(...args: unknown[]): void {
    if (this.pixelStarted) {
      (window as unknown as { fbq?: Fbq }).fbq?.(...args);
    }
  }

  private readConsent(): TrackingConsent | null {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      return stored === 'granted' || stored === 'denied' ? stored : null;
    } catch {
      return null;
    }
  }

  /** Reads the campaign tags of the landing URL; a later page without tags keeps the first ones. */
  private captureAttribution(): StoredAttribution {
    const params = new URLSearchParams(location.search);
    const fromUrl: StoredAttribution = { capturedAt: Date.now() };
    for (const name of UTM_PARAMS) {
      const value = params.get(name)?.trim().slice(0, 150);
      if (value) {
        fromUrl[name] = value;
      }
    }
    const fbclid = params.get('fbclid')?.trim().slice(0, 500);
    if (fbclid) {
      fromUrl.fbclid = fbclid;
    }
    try {
      if (Object.keys(fromUrl).length > 1) {
        sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(fromUrl));
        return fromUrl;
      }
      const stored = JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) ?? 'null');
      return stored && typeof stored === 'object' ? stored : fromUrl;
    } catch {
      return fromUrl;
    }
  }

  private readVisitId(): string {
    const fresh = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    try {
      const stored = sessionStorage.getItem(VISIT_KEY);
      if (stored) {
        return stored;
      }
      sessionStorage.setItem(VISIT_KEY, fresh);
    } catch {
      // No storage: every page load counts as its own visit.
    }
    return fresh;
  }

  /** The site that sent the visitor, when it is not this one. */
  private referrerHost(): string | undefined {
    try {
      const host = new URL(document.referrer).hostname;
      return host && host !== location.hostname ? host.slice(0, 100) : undefined;
    } catch {
      return undefined;
    }
  }

  private cookie(name: string): string | undefined {
    const match = document.cookie.split('; ').find(entry => entry.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  }
}
